// "Já uso o HiperWaba" — regra pura da tela "Configurações › Conectar ao GLA
// Conecta" (sem React). O master decide tudo: se o recurso está liberado, se há
// código vivo, se o número já está conectado. Aqui só se traduz a resposta dele
// no que a tela mostra, e se faz a contagem dos 10 minutos.
//
// Contrato: POST /instances/microservices/conecta/estado e /codigo (spec do
// "Já uso o HiperWaba", §4.2). O código em si só vem na resposta do /codigo: o
// /estado nunca o devolve, por isso um código gerado antes (outra aba, página
// recarregada) aparece mascarado, com "Gerar outro" para ver um novo.

export const PROVIDER_OFICIAL = 'hiperapi-v3-cloud-official'
export const VALIDADE_PADRAO_S = 600
// Abaixo disso o anel fica âmbar: dá tempo, mas é bom correr.
export const POUCO_TEMPO_S = 120
const FUSO = 'America/Sao_Paulo'

// Telas (os nomes seguem o mockup aprovado):
//   inicio        hw-inicio — explica o que muda e gera o código
//   codigo        hw-codigo — código à vista, anel, copiar e gerar outro
//   codigo-oculto hw-codigo sem o código (gerado antes; o master só tem o hash)
//   vencido       hw-vencido — passou o tempo, ou um código novo tomou o lugar
//   usado         o Conecta usou o código e a conexão está terminando por lá
//   conectado     hw-conectado — empresa, data, quem gerou e quem confirmou
//   indisponivel  recurso desligado ou API que não é a oficial
//   falha         não deu para consultar o master
export const TELAS_COM_CONSULTA = ['codigo', 'codigo-oculto', 'usado']

// ── Código ────────────────────────────────────────────────────────────────
export function formatarCodigo(codigo) {
  const c = `${codigo || ''}`.toUpperCase()
  return c.length === 8 ? `${c.slice(0, 4)}-${c.slice(4)}` : c
}

export function partesDoCodigo(codigo) {
  const c = `${codigo || ''}`.toUpperCase()
  return c.length === 8 ? [c.slice(0, 4), c.slice(4)] : [c, '']
}

// Leitor de tela soletra: "K 7 Q M 4 R 2 P" (sem o traço, que não se digita).
export function codigoSoletrado(codigo) {
  return `${codigo || ''}`.toUpperCase().split('').join(' ')
}

// ── Contagem ──────────────────────────────────────────────────────────────
export function mmss(segundos) {
  const s = Math.max(0, Math.floor(Number(segundos) || 0))
  const m = Math.floor(s / 60)
  const r = s % 60
  return `${m < 10 ? '0' : ''}${m}:${r < 10 ? '0' : ''}${r}`
}

export function segundosRestantes(venceEm, agora) {
  if (!Number.isFinite(venceEm) || !Number.isFinite(agora)) return 0
  return Math.max(0, Math.ceil((venceEm - agora) / 1000))
}

export function fracaoDoAnel(restante, total) {
  if (!total || total <= 0) return 0
  return Math.min(1, Math.max(0, restante / total))
}

export function poucoTempo(restante) {
  return restante > 0 && restante <= POUCO_TEMPO_S
}

function instante(valor) {
  const t = valor ? new Date(valor).getTime() : NaN
  return Number.isFinite(t) ? t : NaN
}

// Quando o código vence, no relógio DESTE aparelho. No código recém-gerado, a
// validade (expiraEm − geradoEm) conta a partir de quando a resposta chegou:
// assim um relógio do celular adiantado ou atrasado não encurta nem estica os
// 10 minutos. Sem esse par, cai no expiraEm do servidor.
export function vencimentoLocal({ geradoEm, expiraEm } = {}, recebidoEm) {
  const gerado = instante(geradoEm)
  const expira = instante(expiraEm)
  const validade = expira - gerado
  if (Number.isFinite(validade) && validade > 0 && Number.isFinite(recebidoEm)) {
    return { venceEm: recebidoEm + validade, total: Math.round(validade / 1000) }
  }
  return { venceEm: expira, total: VALIDADE_PADRAO_S }
}

// ── Máquina da tela ───────────────────────────────────────────────────────
export const ESTADO_INICIAL = {
  tela: 'carregando',
  codigo: null,
  geradoEm: null,
  venceEm: NaN,
  total: VALIDADE_PADRAO_S,
  motivoDoVencido: null,
  conexao: null,
  aviso: null
}

function codigoDaResposta(erro) {
  const dados = erro && erro.response && erro.response.data
  return (dados && typeof dados.error === 'string' && dados.error) || null
}

export function reduzir(estado, acao) {
  switch (acao.tipo) {
    case 'estado': {
      const r = acao.resposta || {}
      const situacao = r.situacao
      if (situacao === 'conectado') {
        return { ...ESTADO_INICIAL, tela: 'conectado', conexao: r.conexao || {} }
      }
      if (situacao === 'indisponivel') return { ...ESTADO_INICIAL, tela: 'indisponivel' }

      if (situacao === 'codigo-ativo' && r.codigoAtivo) {
        // O código que está na tela continua sendo o vivo: nada muda. Se a tela
        // tinha concluído "usado" por uma leitura atrasada, o master acaba de
        // desmentir: volta ao código em vez de ficar presa até recarregar.
        if (estado.geradoEm && estado.geradoEm === r.codigoAtivo.geradoEm) {
          if (estado.tela === 'usado' && segundosRestantes(estado.venceEm, acao.agora) > 0) {
            return { ...estado, tela: estado.codigo ? 'codigo' : 'codigo-oculto' }
          }
          return estado
        }
        // Outra aba ou outro aparelho gerou um código novo: este parou de valer.
        if (estado.codigo && estado.tela === 'codigo') {
          return { ...estado, tela: 'vencido', motivoDoVencido: 'outro' }
        }
        const venceEm = instante(r.codigoAtivo.expiraEm)
        const gerado = instante(r.codigoAtivo.geradoEm)
        const total = Number.isFinite(venceEm - gerado) && venceEm > gerado ? Math.round((venceEm - gerado) / 1000) : VALIDADE_PADRAO_S
        return { ...ESTADO_INICIAL, tela: 'codigo-oculto', geradoEm: r.codigoAtivo.geradoEm, venceEm, total }
      }

      // "disponivel": não há código vivo. Se havia um na tela ainda com tempo,
      // ele foi USADO no Conecta (o master só deixa de considerá-lo vivo assim)
      // e a conexão está terminando por lá.
      if (estado.tela === 'codigo' || estado.tela === 'codigo-oculto') {
        const restante = segundosRestantes(estado.venceEm, acao.agora)
        if (restante > 5) return { ...estado, tela: 'usado', aviso: null }
        return { ...estado, tela: 'vencido', motivoDoVencido: 'tempo' }
      }
      if (estado.tela === 'usado' || estado.tela === 'vencido') return estado
      return { ...ESTADO_INICIAL, tela: 'inicio', aviso: estado.aviso }
    }

    case 'gerado': {
      const { venceEm, total } = vencimentoLocal(acao.resposta, acao.agora)
      return {
        ...ESTADO_INICIAL,
        tela: 'codigo',
        codigo: acao.resposta.codigo,
        geradoEm: acao.resposta.geradoEm,
        venceEm,
        total
      }
    }

    case 'tique': {
      if ((estado.tela === 'codigo' || estado.tela === 'codigo-oculto') && segundosRestantes(estado.venceEm, acao.agora) <= 0) {
        return { ...estado, tela: 'vencido', motivoDoVencido: 'tempo' }
      }
      return estado
    }

    case 'falhaAoGerar': {
      const codigo = codigoDaResposta(acao.erro)
      const dados = (acao.erro && acao.erro.response && acao.erro.response.data) || {}
      if (codigo === 'ERR_JA_CONECTADO') return { ...ESTADO_INICIAL, tela: 'conectado', conexao: dados.conexao || {} }
      if (codigo === 'ERR_PAREAMENTO_INDISPONIVEL' || codigo === 'ERR_NAO_OFICIAL') return { ...ESTADO_INICIAL, tela: 'indisponivel' }
      if (codigo === 'ERR_LIMITE_DE_CODIGOS') return { ...estado, aviso: { tipo: 'limite', liberaEm: dados.liberaEm || null } }
      return { ...estado, aviso: { tipo: 'rede' } }
    }

    case 'falhaAoCarregar':
      // Uma consulta de acompanhamento que falha não derruba a tela que já está aberta.
      return estado.tela === 'carregando' ? { ...ESTADO_INICIAL, tela: 'falha' } : estado

    case 'recarregar':
      return { ...ESTADO_INICIAL }

    case 'limparAviso':
      return estado.aviso ? { ...estado, aviso: null } : estado

    default:
      return estado
  }
}

// ── Textos ────────────────────────────────────────────────────────────────
export function horaCurta(valor) {
  const t = instante(valor)
  if (!Number.isFinite(t)) return null
  return new Date(t).toLocaleTimeString('pt-BR', { timeZone: FUSO, hour: '2-digit', minute: '2-digit' })
}

export function dataEHora(valor) {
  const t = instante(valor)
  if (!Number.isFinite(t)) return null
  return new Date(t).toLocaleString('pt-BR', {
    timeZone: FUSO,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

export function textoDoAviso(aviso) {
  if (!aviso) return null
  if (aviso.tipo === 'limite') {
    const hora = horaCurta(aviso.liberaEm)
    return hora
      ? `Você gerou muitos códigos na última hora. Dá para gerar outro a partir das ${hora}.`
      : 'Você gerou muitos códigos na última hora. Espere um pouco e tente de novo.'
  }
  return 'Não conseguimos falar com o servidor agora. Confira a internet e tente de novo.'
}

export function fraseDoVencido(motivo) {
  return motivo === 'outro'
    ? 'Um código novo foi gerado em outra aba ou aparelho, e este parou de valer. Nada foi alterado no seu número.'
    : 'Este código venceu e não funciona mais. Não se preocupe: nada foi alterado no seu número.'
}

// Conectado pela GLA (Vincular do painel admin) ou só detectado na Meta: não há
// código nem quem confirmou, e a tela diz isso em vez de deixar campos vazios.
export function feitoPeloCliente(conexao) {
  return Boolean(conexao) && conexao.origem === 'pareamento'
}

export function fraseDaConexao(conexao) {
  const empresa = conexao && conexao.empresaNome
  return empresa
    ? `Este número já está conectado ao GLA Conecta, na empresa ${empresa}.`
    : 'Este número já está conectado ao GLA Conecta.'
}

// As linhas de "Detalhes da conexão". Campo que o master não mandou não vira
// linha com traço: some.
export function detalhesDaConexao(conexao) {
  const c = conexao || {}
  const linhas = []
  if (c.empresaNome) linhas.push({ rotulo: 'Empresa no Conecta', valor: c.empresaNome })
  if (c.telefoneMascarado) linhas.push({ rotulo: 'Número', valor: c.telefoneMascarado })
  const quando = dataEHora(c.conectadoEm)
  if (quando) linhas.push({ rotulo: 'Conectado em', valor: quando })
  if (feitoPeloCliente(c)) {
    // Não existe usuário individual no painel (entra-se com api + secret): quem
    // gerou é a CONTA, pelo e-mail do cadastro do cliente.
    linhas.push({
      rotulo: 'Código gerado por',
      valor: c.geradoPor ? `Gerado no painel do HiperWaba · conta ${c.geradoPor}` : 'Gerado no painel do HiperWaba'
    })
    if (c.confirmadoPor) linhas.push({ rotulo: 'Confirmado no Conecta por', valor: c.confirmadoPor })
  } else {
    linhas.push({ rotulo: 'Conectado por', valor: 'Conectado pela GLA' })
  }
  linhas.push({ rotulo: 'Cobrança do atendimento', valor: c.cobranca || 'No saldo desta API' })
  return linhas
}

// ── Cartão em Configurações ───────────────────────────────────────────────
// Só para a API oficial, e só quando o master não diz "indisponível". O master
// vem ligado por padrão; o freio CONECTA_PAREAMENTO_LIGADO = "0" (ou falta de
// chave do HMAC / app da Meta) faz o estado dizer "indisponível" e esconde tudo.
export function mostrarCartao(provider, resposta) {
  return provider === PROVIDER_OFICIAL && Boolean(resposta) && Boolean(resposta.situacao) && resposta.situacao !== 'indisponivel'
}

export function seloDoCartao(resposta) {
  const s = resposta && resposta.situacao
  if (s === 'conectado') return { texto: 'Conectado', tom: 'ok' }
  if (s === 'codigo-ativo') return { texto: 'Código ativo', tom: 'ok' }
  return null
}

// ── Falar com a GLA ───────────────────────────────────────────────────────
// O número vem do build (REACT_APP_GLA_WHATSAPP, só dígitos). Sem ele, o botão
// não aparece e o texto continua dizendo para falar com a GLA — mesma regra do
// "Falar com a GLA" do Conecta, que some sem o telefone da plataforma.
export const MENSAGEM_PARA_A_GLA = 'Olá! Preciso de ajuda com a conexão do meu número do HiperWaba no GLA Conecta.'

export function linkDaGla(numero) {
  const digitos = `${numero || ''}`.replace(/\D/g, '')
  return digitos.length >= 10 ? `https://wa.me/${digitos}?text=${encodeURIComponent(MENSAGEM_PARA_A_GLA)}` : null
}
